import { sync } from '@flashka/shared/sync-client/sync';

import { useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useEventCallback, useEventListener } from 'usehooks-ts';

import { useAuth } from '@/auth/better/session';
import { UserInfo } from '@/components/dashboard/user-info/hooks';

import { AUTO_DETECT } from '../dashboard/user-info/enums';

export type UserInfoWithAiLanguage = UserInfo & { aiLanguage: string };

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent;
  }
}

export type SetValue<T> = Dispatch<SetStateAction<T>>;

// A stored aiLanguage of the literal string "NULL"/"null" (from a bad past
// migration that stringified SQL NULL into Supabase) would otherwise flow
// into AI requests as `language: "NULL"`, making the model literally answer
// "NULL". Treat those as missing.
const sanitizeAiLanguage = (raw: string | null | undefined): string => {
  if (!raw || raw === 'NULL' || raw === 'null') return AUTO_DETECT;
  return raw;
};

export const useGetUserLanguage = (): {
  language: string;
  aiLanguage: string;
} => {
  const { userId } = useAuth();
  const userInfo = sync.userInfo.use();
  const isHydrated = sync.store.useIsHydrated();
  const legacyLanguage = useLocalStorage('user-language', '')[0];

  // Also check localStorage for backward compat
  const [localStorageInfo] = useLocalStorage<Record<string, UserInfoWithAiLanguage>>(
    'user-info',
    {},
  );

  // Prefer sync store if hydrated and has language
  if (isHydrated && userInfo?.language) {
    return {
      language: userInfo.language,
      aiLanguage: sanitizeAiLanguage(userInfo.aiLanguage),
    };
  }

  // Fallback to localStorage
  if (userId && localStorageInfo && localStorageInfo[userId]) {
    return {
      language: localStorageInfo[userId]?.language ?? legacyLanguage,
      aiLanguage: sanitizeAiLanguage(localStorageInfo[userId]?.aiLanguage),
    };
  }

  return { language: legacyLanguage, aiLanguage: AUTO_DETECT };
};

export const useUpdateUserLanguage = () => {
  const { userId } = useAuth();

  return useMutation<void, Error, { newLanguage: string; newAiLanguage: string }>({
    mutationFn: async ({ newLanguage, newAiLanguage }) => {
      if (!userId) {
        throw new Error('missing userId!');
      }

      // Update via sync store (auto-syncs to DO)
      const current = sync.userInfo.get();
      sync.userInfo.set({
        ...current,
        language: newLanguage,
        aiLanguage: newAiLanguage,
      });
    },
  });
};

export function useSessionStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback((): T => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.sessionStorage.getItem(key);
      return item ? (parseJSON(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading sessionStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue: SetValue<T> = useEventCallback((value) => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined') {
      console.warn(
        `Tried setting sessionStorage key “${key}” even though environment is not a client`,
      );
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value;

      // Save to local storage
      window.sessionStorage.setItem(key, JSON.stringify(newValue));

      // Save state
      setStoredValue(newValue);

      // We dispatch a custom event so every useSessionStorage hook are notified
      window.dispatchEvent(new Event('session-storage'));
    } catch (error) {
      console.warn(`Error setting sessionStorage key “${key}”:`, error);
    }
  });

  useEffect(() => {
    setStoredValue(readValue());
  }, []);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue],
  );

  // this only works for other documents, not the current one
  useEventListener('storage', handleStorageChange);

  // this is a custom event, triggered in writeValueToSessionStorage
  // See: useSessionStorage()
  useEventListener('session-storage', handleStorageChange);

  return [storedValue, setValue];
}

export function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback((): T => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (parseJSON(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: SetValue<T> = useEventCallback((value) => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined') {
      console.warn(
        `Tried setting localStorage key “${key}” even though environment is not a client`,
      );
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value;

      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(newValue));

      // Save state
      setStoredValue(newValue);

      // We dispatch a custom event so every useLocalStorage hook are notified
      window.dispatchEvent(new Event('local-storage'));
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }
  });

  // Re-read when the KEY changes (a per-account key that lands after mount),
  // not on every `initialValue` identity — a fresh `{}` literal per render
  // would loop.
  useEffect(() => {
    setStoredValue(readValue());
  }, [key]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue],
  );

  // this only works for other documents, not the current one
  useEventListener('storage', handleStorageChange);

  // this is a custom event, triggered in writeValueToLocalStorage
  // See: useLocalStorage()
  useEventListener('local-storage', handleStorageChange);

  return [storedValue, setValue];
}

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    return undefined;
  }
}

export const readLocalStorage = (key: string) => {
  // Prevent build error "window is undefined" but keeps working
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const item = window.localStorage.getItem(key);
    return item ? parseJSON(item) : undefined;
  } catch (error) {
    console.warn(`Error reading localStorage key “${key}”:`, error);
    return undefined;
  }
};

export const setLocalStorage = (key: string, newValue: unknown) => {
  // Prevent build error "window is undefined" but keeps working
  if (typeof window === 'undefined') {
    console.warn(
      `Tried setting localStorage key “${key}” even though environment is not a client`,
    );
  }

  try {
    // Save to local storage
    window.localStorage.setItem(key, JSON.stringify(newValue));
  } catch (error) {
    console.warn(`Error setting localStorage key “${key}”:`, error);
  }
};
